import React, { Fragment, useContext } from "react";
import PositionItem from "./PositionItem";
import {WindowContext} from "../../Service/Context/WindowContext";
import PositionSum from "./PositionSum";
import PositionGroupSum from "./PositionGroupSum";
import ProfileInjectWrapper from "../Profile/ProfileInjectWrapper";
import { groupPositions } from "./groupPositions";
import { getDocumentTranslator } from "../Document/documentLanguage";
import { useLingui } from "@lingui/react";
import {
    shouldHideQuantityAndPriceColumns,
    shouldShowOptionalPositionColumn,
} from "./positionDisplay";

const PositionList = ({formSubmission, resourceId, positions, positionGroups, taxType, isSummable, values, handleChange, patchSubmissionValue, document, onPositionChange}) => {
    const {windowWidth} = useContext(WindowContext);
    const { i18n } = useLingui();
    const t = getDocumentTranslator(i18n, document.language);

    if (!positions) {
        return null;
    }

    if (positions.length === 0) {
        return null;
    }

    const hideQuantityAndPriceColumns = shouldHideQuantityAndPriceColumns(
        positions,
        document.documentType
    );
    const showOptionalPositionColumn = shouldShowOptionalPositionColumn(positions);
    const horizontalPadding = windowWidth < 800 ? 10 : 14;

    let styles = {
        container: {
          marginTop: 24,
          marginBottom: 24,
        },
        firstColumn: {
            width: showOptionalPositionColumn ? 50 : 0,
            flexShrink: 0,
            display: "flex",
            justifyContent: "center",
        },
        textColumn: {
            flex: 1,
            minWidth: 0,
        },
        priceColumn: {
            width: windowWidth < 800 ? 110 : (windowWidth > 1100 ? 130 : 120),
            flexShrink: 0,
            textAlign: "right"
        },
        alignedContent: {
            paddingLeft: horizontalPadding,
            paddingRight: horizontalPadding,
        },
        columnHeader: {
            display: "flex",
            paddingLeft: horizontalPadding,
            paddingRight: horizontalPadding,
        },
        divider: {
            height: 1,
            margin: 0,
            border: 0,
            backgroundColor: "#e9e9e9",
        },
        sumDivider: {
            height: 1,
            margin: "16px 0",
            border: 0,
            backgroundColor: "#b8b8b8",
        },
        group: {
            margin: "16px 0 22px",
            overflow: "hidden",
            border: "1px solid #E2E2E2",
            borderRadius: 4,
            backgroundColor: "#ffffff",
        },
        groupHeader: {
            display: "flex",
            alignItems: "center",
            minHeight: 40,
            padding: windowWidth < 800 ? "6px 12px" : "6px 16px",
            borderBottom: "1px solid #dedede",
            backgroundColor: "#f3f3f3",
        },
        groupName: {
            flex: 1,
            minWidth: 0,
            color: "#30343a",
            fontSize: windowWidth < 800 ? 15 : 16,
            fontWeight: 700,
            lineHeight: 1.3,
            margin: 0,
            overflowWrap: "break-word",
        },
        groupBody: {
            padding: `${windowWidth < 800 ? 4 : 6}px ${horizontalPadding}px 0`,
        },
        ungroupedPositions: {
            marginTop: 8,
            paddingLeft: horizontalPadding,
            paddingRight: horizontalPadding,
        },
        positionsWithoutGroup: {
            paddingLeft: horizontalPadding,
            paddingRight: horizontalPadding,
        }
    };

    const groupedPositions = groupPositions(positions, positionGroups);

    const renderPositions = (positionsToRender, hideLastDivider = false) => positionsToRender.map((position, index) => (
        <Fragment key={position.id}>
            <PositionItem _document={document} taxType={taxType} formSubmission={formSubmission} values={values} position={position} handleChange={handleChange} patchSubmissionValue={patchSubmissionValue} columnStyles={styles} onPositionChange={onPositionChange} />
            {position.type !== 'FREE_TEXT' && (!hideLastDivider || index < positionsToRender.length - 1) && <hr style={styles.divider}/>}
        </Fragment>
    ));

    return (
        <div style={styles.container}>
            <div style={styles.columnHeader}>
                {showOptionalPositionColumn && <div style={styles.firstColumn}/>}
                <div style={styles.textColumn}>{t("description")}</div>
                {windowWidth > 800 && !hideQuantityAndPriceColumns && <div style={styles.priceColumn}>{t("amount")}</div>}
                {windowWidth > 800 && !hideQuantityAndPriceColumns && <div style={styles.priceColumn}>{t("price")}</div>}
                <div style={styles.priceColumn}>{t("total")}</div>
            </div>
            <hr/>
            {groupedPositions ? (
                <>
                    {groupedPositions.groups.map(({group, positions: positionsInGroup}) => (
                        <section key={group.id} style={styles.group} aria-label={group.name}>
                            <div style={styles.groupHeader}>
                                <h3 style={styles.groupName}>{group.name}</h3>
                            </div>
                            <div style={styles.groupBody}>
                                {renderPositions(positionsInGroup, isSummable)}
                            </div>
                            <PositionGroupSum
                                isSummable={isSummable}
                                document={document}
                                groupName={group.name}
                                positions={positionsInGroup}
                                values={values}
                                columnStyles={styles}
                            />
                        </section>
                    ))}
                    {groupedPositions.ungroupedPositions.length > 0 && (
                        <div style={styles.ungroupedPositions}>
                            {renderPositions(groupedPositions.ungroupedPositions, isSummable)}
                        </div>
                    )}
                </>
            ) : (
                <div style={styles.positionsWithoutGroup}>
                    {renderPositions(positions, isSummable)}
                </div>
            )}
            {isSummable && <hr style={styles.sumDivider}/>}
            <ProfileInjectWrapper resourceId={resourceId}>
                <PositionSum isSummable={isSummable} document={document} resourceId={resourceId} values={values} positions={positions} columnStyles={styles} />
            </ProfileInjectWrapper>
        </div>

    );
};

export default PositionList;
